import { useState, useEffect, useCallback } from 'react';
import { getRepository } from '../../config/repository';
import { MenuSettings, DEFAULT_MENU_SETTINGS, SECONDARY_LANGUAGES } from './types';

/** Event name dispatched when menu settings change */
export const MENU_SETTINGS_UPDATED = 'menu-settings-updated';

export interface MenuLocalization {
  /** e.g. 'Arabic', 'Spanish', 'French' */
  secondaryLangName: string;
  /** e.g. 'AR', 'ES', 'FR' */
  secondaryLangCode: string;
  /** e.g. 'العربية', 'Español' */
  secondaryLangNative: string;
  /** 'rtl' or 'ltr' */
  secondaryDir: 'rtl' | 'ltr';
  /** e.g. 'QAR', 'USD', 'AED' */
  currencySymbol: string;
  /** Full settings object */
  settings: MenuSettings;
}

const DEFAULT_LANG = SECONDARY_LANGUAGES.find(l => l.code === 'AR')!;

const defaults: MenuLocalization = {
  secondaryLangName: DEFAULT_LANG.name,
  secondaryLangCode: DEFAULT_LANG.code,
  secondaryLangNative: DEFAULT_LANG.nativeName,
  secondaryDir: DEFAULT_LANG.dir,
  currencySymbol: DEFAULT_MENU_SETTINGS.currency,
  settings: DEFAULT_MENU_SETTINGS,
};

export function useMenuLocalization(): MenuLocalization {
  const [loc, setLoc] = useState<MenuLocalization>(defaults);

  const load = useCallback(async () => {
    const all = await getRepository().getAll<MenuSettings>('menu-settings');
    const s = all[0] || DEFAULT_MENU_SETTINGS;
    const lang = SECONDARY_LANGUAGES.find(l => l.code === s.secondaryLanguage) || DEFAULT_LANG;
    setLoc({
      secondaryLangName: lang.name,
      secondaryLangCode: lang.code,
      secondaryLangNative: lang.nativeName,
      secondaryDir: lang.dir,
      currencySymbol: s.currency,
      settings: s,
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  // Re-load when settings are updated from the Settings tab
  useEffect(() => {
    const handler = () => load();
    window.addEventListener(MENU_SETTINGS_UPDATED, handler);
    return () => window.removeEventListener(MENU_SETTINGS_UPDATED, handler);
  }, [load]);

  return loc;
}
